import { range } from "@prismicio/editor-support/Array"
import { groupBy } from "@prismicio/editor-support/Map"
import { useOnChange } from "@prismicio/editor-support/React"
import {
  Badge,
  Box,
  Button,
  ComboBox,
  ComboboxAction,
  ComboBoxContent,
  ComboBoxInput,
  ComboBoxItem,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
  ErrorBoundary,
  Skeleton,
  Text,
} from "@prismicio/editor-ui"
import { Suspense, useMemo, useState } from "react"

import { type Tag, useTagSearch } from "../../../hooks/tagData"
import { useMediaLibrary } from "../../MediaLibraryContext"

interface SelectedTagsListProps {
  optimisticTagIds: string[]
  setOptimisticTagIds: (tagIds: string[]) => void
}

function SelectedTagsList(props: SelectedTagsListProps) {
  const { optimisticTagIds, setOptimisticTagIds } = props

  const { onFilterChange } = useMediaLibrary()

  const { tags } = useTagSearch()

  const tagsMap = useMemo(() => groupBy(tags, (tag) => tag.id), [tags])

  function onRemoveTag(tag: Tag) {
    const newTagIds = optimisticTagIds.filter((tagId) => tagId !== tag.id)
    setOptimisticTagIds(newTagIds)
    onFilterChange({ tagIds: newTagIds })
  }

  return (
    <>
      {optimisticTagIds.map((tagId) => {
        const tag = tagsMap.get(tagId)?.[0]
        if (!tag) return null

        return (
          <Badge
            key={tag.id}
            title={tag.name}
            size="medium"
            color="purple"
            onClose={() => onRemoveTag(tag)}
            selected
          />
        )
      })}
    </>
  )
}

export function TagsFilter() {
  const { setTagManagerOpen, tagIds, onFilterChange } = useMediaLibrary()

  const [tagSearchTerm, setTagSearchTerm] = useState("")
  const [tagDropdownOpen, setTagDropdownOpen] = useState(false)
  const [optimisticTagIds, setOptimisticTagIds] = useState(tagIds)

  useOnChange(JSON.stringify(tagIds), () => {
    setOptimisticTagIds(tagIds)
  })

  function onOpenChange(newOpen: boolean) {
    setTagDropdownOpen(newOpen)
    if (!newOpen) setTagSearchTerm("")
  }

  function onTagToggle(tag: Tag) {
    const newTagIds = optimisticTagIds.includes(tag.id)
      ? tagIds.filter((tagId) => tagId !== tag.id)
      : [...tagIds, tag.id]
    setOptimisticTagIds(newTagIds)
    onFilterChange({ tagIds: newTagIds })
    setTagDropdownOpen(false)
  }

  function onManageTags() {
    setTagDropdownOpen(false)
    setTagManagerOpen(true)
  }

  return (
    <>
      <DropdownMenu open={tagDropdownOpen} onOpenChange={onOpenChange}>
        <DropdownMenuTrigger>
          <Button
            startIcon="label"
            endIcon="arrowDropDown"
            size="small"
            color="grey"
          >
            Tags
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          minWidth={256}
          align="start"
          border={false}
          childrenFocusScope
        >
          <ComboBox variant="attached">
            <ComboBoxInput
              value={tagSearchTerm}
              onValueChange={setTagSearchTerm}
              placeholder="Search for a tag"
              // The combobox requires the input to be focused in order for
              // it to be keyboard navigateable.
              // setting autofocus means on open of the dropdown menu, we immediately
              // focus into the combobox so that users can keyboard navigate
              // eslint-disable-next-line jsx-a11y/no-autofocus
              autoFocus
            />
            <ComboBoxContent paddingBlock={4}>
              <ErrorBoundary
                renderError={() => (
                  <Text
                    color="grey9"
                    variant="small"
                    sx={{ marginBlock: 8, marginInline: 8 }}
                  >
                    Error getting tags
                  </Text>
                )}
              >
                <Suspense
                  fallback={
                    <Box padding={8} flexDirection="column" gap={8}>
                      {range(1, 6).map((num) => (
                        <Skeleton key={num} height={24} width={85} />
                      ))}
                    </Box>
                  }
                >
                  <TagList
                    searchTerm={tagSearchTerm}
                    setSearchTerm={setTagSearchTerm}
                    onTagToggle={onTagToggle}
                    optimisticTagIds={optimisticTagIds}
                  />
                </Suspense>
              </ErrorBoundary>
            </ComboBoxContent>
            <ComboboxAction>
              <Button
                invisible
                startIcon="edit"
                textWeight="normal"
                color="dark"
                onClick={onManageTags}
              >
                Manage tags
              </Button>
            </ComboboxAction>
          </ComboBox>
        </DropdownMenuContent>
      </DropdownMenu>
      {optimisticTagIds.length > 0 && (
        <ErrorBoundary>
          <Suspense>
            <SelectedTagsList
              optimisticTagIds={optimisticTagIds}
              setOptimisticTagIds={setOptimisticTagIds}
            />
          </Suspense>
        </ErrorBoundary>
      )}
    </>
  )
}

interface TagListProps {
  onTagToggle: (tag: Tag) => void
  searchTerm: string
  setSearchTerm: (searchTerm: string) => void
  optimisticTagIds: string[]
}

function TagList(props: TagListProps) {
  const { onTagToggle, searchTerm, optimisticTagIds } = props

  const { tags } = useTagSearch()

  const filteredTags = useMemo(() => {
    const searchTermLowerCase = searchTerm.toLowerCase()
    return tags.filter((tag) =>
      tag.name.toLowerCase().includes(searchTermLowerCase),
    )
  }, [tags, searchTerm])

  return (
    <>
      {!filteredTags.length && (
        <Text
          color="grey9"
          variant="small"
          sx={{ marginBlock: 8, marginInline: 8 }}
        >
          No tags found
        </Text>
      )}
      {filteredTags.map((tag) => {
        const checked = optimisticTagIds.includes(tag.id)
        return (
          <ComboBoxItem
            key={tag.id}
            value={tag.id}
            onCheckedChange={() => onTagToggle(tag)}
            checked={checked}
          >
            <Badge
              title={tag.name}
              color="purple"
              sx={{
                marginLeft: 8,
                marginBottom: 4,
                marginTop: 4,
              }}
              size="medium"
            />
          </ComboBoxItem>
        )
      })}
    </>
  )
}
